/**
 * Calendar features beyond the core items (settings, navigation layout, …): the API surface, the real
 * implementation over the platform API and the in-memory mock (`VITE_USE_MOCK=1`).
 */
import { ApiError, type CalendarEvent, type Category, type EventAttachment, type CalendarEventChanges, type CalendarLayout, type CalendarSettings, type CalendarSettingsPatch, type ForwardMeetingInput, type ProposedTime, type ProposeTimeInput, type ProposeTimeResult, type WebmailClient } from '@platform/api-client';
import type { RegionalPreferences } from '@platform/api-client';
export type { RegionalPreferences };

export type { CalendarLayout, CalendarSettings, CalendarSettingsPatch, Category, EventAttachment };

/** What the mock extras reuse from the mock calendar. */
type MockBase = {
  updateEvent(id: string, changes: CalendarEventChanges, scope?: 'occurrence' | 'series', notify?: boolean): Promise<CalendarEvent>;
  listEvents(calendarIds: readonly string[], start: string, end: string): Promise<{ calendarId: string; events: CalendarEvent[] }[]>;
};

export interface CalendarExtraApi {
  regionalSettings(): Promise<RegionalPreferences>;
  saveRegionalSettings(value: RegionalPreferences): Promise<RegionalPreferences>;
  calendarSettings(): Promise<CalendarSettings>;
  updateCalendarSettings(patch: CalendarSettingsPatch): Promise<CalendarSettings>;
  calendarLayout(): Promise<CalendarLayout>;
  setCalendarLayout(layout: CalendarLayout): Promise<CalendarLayout>;
  /** "This and following": a new series from this occurrence on, the old one ends the day before. */
  splitSeries(eventId: string, changes: CalendarEventChanges, notify?: boolean): Promise<CalendarEvent>;
  /** Attendee: answer and propose a new time to the organizer. */
  proposeNewTime(eventId: string, input: ProposeTimeInput): Promise<ProposeTimeResult>;
  forwardMeeting(eventId: string, input: ForwardMeetingInput): Promise<void>;
  /** Proposed time of a meeting response message (undefined: none). */
  meetingProposal(messageId: string): Promise<ProposedTime | undefined>;
  /** Events whose subject or location contains the text (series once), per calendar. */
  searchEvents(calendarIds: readonly string[], query: string): Promise<{ calendarId: string; events: CalendarEvent[]; error?: { code: string; message: string } | undefined }[]>;
  /** File attachments of an event (download link via eventAttachmentUrl). */
  eventAttachments(eventId: string): Promise<EventAttachment[]>;
  addEventAttachment(eventId: string, file: { name: string; contentType: string; content: string }): Promise<EventAttachment[]>;
  removeEventAttachment(eventId: string, attachmentId: string): Promise<void>;
  eventAttachmentUrl(eventId: string, attachmentId: string): string;
  /** The user's categories (name + colour). */
  calendarCategories(): Promise<Category[]>;
  setCalendarCategories(categories: readonly Category[]): Promise<Category[]>;
}

/** The server's defaults (services/platform-api modules/calendar/preferences.ts), used until settings load. */
export const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = {
  weekStart: null,
  timeFormat: 'locale',
  workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  workingStartMinutes: 480,
  workingEndMinutes: 1020,
  hourHeight: 3,
  fitWorkingHours: false,
  defaultDurationMinutes: 30,
  defaultReminderMinutes: 15,
  displayTimeZone: null,
  secondaryTimeZone: null,
  weekNumbers: false,
  weekNumbering: 'regional',
  hideDeclined: false,
  customDays: 4,
  remindersEnabled: true,
};

export function createRealCalendarExtra(client: WebmailClient): CalendarExtraApi {
  return {
    regionalSettings: () => client.regionalSettings(),
    saveRegionalSettings: (value) => client.saveRegionalSettings(value),
    calendarSettings: () => client.calendarSettings(),
    updateCalendarSettings: (patch) => client.updateCalendarSettings(patch),
    calendarLayout: () => client.calendarLayout(),
    setCalendarLayout: (layout) => client.setCalendarLayout(layout),
    splitSeries: (eventId, changes, notify = true) => client.splitCalendarSeries(eventId, { changes, notify }),
    proposeNewTime: (eventId, input) => client.proposeNewTime(eventId, input),
    searchEvents: async (calendarIds, query) => (await client.searchCalendarEvents(calendarIds.join(','), query)).results,
    eventAttachments: async (eventId) => (await client.eventAttachments(eventId)).attachments,
    addEventAttachment: async (eventId, file) => (await client.addEventAttachment(eventId, file)).attachments,
    removeEventAttachment: async (eventId, attachmentId) => {
      await client.removeEventAttachment(eventId, attachmentId);
    },
    eventAttachmentUrl: (eventId, attachmentId) => client.eventAttachmentUrl(eventId, attachmentId),
    calendarCategories: async () => (await client.calendarCategories()).categories,
    setCalendarCategories: async (categories) => (await client.setCalendarCategories({ categories: [...categories] })).categories,
    forwardMeeting: async (eventId, input) => {
      await client.forwardMeeting(eventId, input);
    },
    meetingProposal: async (messageId) => {
      try {
        return await client.meetingProposal(messageId);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return undefined;
        throw error;
      }
    },
  };
}

const delay = <T>(value: T, ms = 60): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));

export function createMockCalendarExtra(base: MockBase): CalendarExtraApi {
  let regional: RegionalPreferences = { locale: null, displayTimeZone: null, weekNumbering: 'regional', setup: 'pending' };
  let settings: CalendarSettings = { ...DEFAULT_CALENDAR_SETTINGS };
  let layout: CalendarLayout = { groups: [], order: [] };
  const attachments = new Map<string, EventAttachment[]>();
  let categories: Category[] = [
    { name: 'Customer', color: 'orange' },
    { name: 'Internal', color: 'purple' },
  ];
  return {
    regionalSettings: () => delay({ ...regional, displayTimeZone: settings.displayTimeZone, weekNumbering: settings.weekNumbering ?? 'regional' }),
    saveRegionalSettings: (value) => {
      regional = { ...value };
      settings = { ...settings, displayTimeZone: value.displayTimeZone, weekNumbering: value.weekNumbering };
      return delay(regional);
    },
    calendarSettings: () => delay(settings),
    updateCalendarSettings: (patch) => {
      settings = { ...settings, ...patch };
      return delay(settings);
    },
    calendarLayout: () => delay(layout),
    setCalendarLayout: (next) => {
      layout = next;
      return delay(layout);
    },
    // Preview mode: the whole series changes (no split in the mock).
    splitSeries: (eventId, changes, notify) => base.updateEvent(eventId, changes, 'series', notify),
    proposeNewTime: () => delay({ delivery: 'proposed' as const }),
    // Preview mode: half a year back and ahead, each series once.
    searchEvents: async (calendarIds, query) => {
      const now = Date.now();
      const results = await base.listEvents(calendarIds, new Date(now - 180 * 86_400_000).toISOString(), new Date(now + 60 * 86_400_000).toISOString());
      const needle = query.toLowerCase();
      return results.map(({ calendarId, events }) => {
        const seen = new Set<string>();
        const found = events.filter((e) => !e.masked && (e.subject.toLowerCase().includes(needle) || e.location.toLowerCase().includes(needle)) && !seen.has(e.seriesId ?? e.id) && seen.add(e.seriesId ?? e.id));
        return { calendarId, events: found.reverse() };
      });
    },
    eventAttachments: (eventId) => delay(attachments.get(eventId) ?? []),
    addEventAttachment: (eventId, file) => {
      const list = [...(attachments.get(eventId) ?? []), { id: `att-${Date.now()}`, name: file.name, size: Math.floor((file.content.length * 3) / 4), contentType: file.contentType }];
      attachments.set(eventId, list);
      return delay(list);
    },
    removeEventAttachment: (eventId, attachmentId) => {
      attachments.set(eventId, (attachments.get(eventId) ?? []).filter((a) => a.id !== attachmentId));
      return delay(undefined);
    },
    eventAttachmentUrl: () => '#',
    calendarCategories: () => delay(categories),
    setCalendarCategories: (next) => {
      categories = [...next];
      return delay(categories);
    },
    forwardMeeting: () => delay(undefined),
    meetingProposal: () => delay(undefined),
  };
}
